import * as _ from 'lodash'
import { IBusinessObject } from '../PA.Core/PA.Core.IF.BO';
import { IMessageProvider, IProgressProvider, MessageType } from '../PA.Core/PA.Core.IF.Basic';
import { PAdbContext } from '../PA.Core/PAdbContext';
import { IdentityProvider } from '../PA.Core/Basic/Identity'
import { MessageProvider } from '../PA.Core/Basic/Messages'
import { ProgressProvider } from '../PA.Core/Basic/Progress'
import {
    WB_PPA_ProductRow,
    WB_PPA_Product_AttributeRow, WB_PPA_AttributeRow
} from "../PA.Core/PAdbSchemaRows";
import { IwbPPAProduct, IwbPPAScenario } from "../PA.Core/PAdbSchemaRowsIF";
import {
    esWB_PPA_Attribute,
    esWB_PPA_Product,
    esWB_PPA_Product_Attribute, esWB_PPA_Scenario, esWB_PPA_Scenario_Prd
} from "../PA.Core/PAdbSchema";
import { ESRecord } from "../PA.Core/lib/ESRecord";
import { ESDataStore } from "../PA.Core/lib/ESDataStore";
import { CreateOptions, ESRecordResolver } from "../PA.Core/lib/ESRecordResolver";
import { ASync } from "../PA.Core/Basic/ASync";
import { PAEnvironment } from "../PA.Core/PAEnvironment";
import { PAEntity } from "../PA.Core/PAEntity";

export class PPAProductBO implements IBusinessObject {
    Identity: IdentityProvider;

    CreateOptions: any;

    LoadOptions: any;
    _bIsLoaded: boolean;
    _OnLoadComplete: Promise<IBusinessObject>;
    LoadProgressProvider: IProgressProvider;

    ValidateOptions: any;
    _bIsValidated: boolean;
    ValidateMessageProvider: IMessageProvider;
    ValidateProgressProvider: IProgressProvider;

    SaveOptions: any;
    _bIsSaved: boolean;
    SaveProgressProvider: IProgressProvider;

    DeleteOptions: any;
    _bIsDeleted: boolean;
    DeleteMessageProvider: IMessageProvider;
    DeleteProgressProvider: IProgressProvider;

    dbContext: PAdbContext;
    drWB_PPAProduct: WB_PPA_ProductRow;

    _bIsIdEditable: boolean;

    constructor(options?: any) {

        this.CreateOptions = options || {};

        if ('dbContext' in this.CreateOptions)
            this.dbContext = this.CreateOptions.dbContext;

        this.Identity = new IdentityProvider({ id: 0, name: 'New Spend Function' });

        this.LoadOptions = {};
        this._bIsLoaded = false;
        this._OnLoadComplete = null;
        this.LoadProgressProvider = new ProgressProvider('Loading', this);

        this.ValidateOptions = {};
        this._bIsValidated = false;
        this.ValidateMessageProvider = new MessageProvider();
        this.ValidateProgressProvider = new ProgressProvider("Validating", this);

        this.SaveOptions = {};
        this._bIsSaved = false;
        this.SaveProgressProvider = new ProgressProvider("Saving", this);

        this.DeleteOptions = {};
        this._bIsDeleted = false;
        this.DeleteMessageProvider = new MessageProvider();
        this.DeleteProgressProvider = new ProgressProvider("Deleting", this);

        this.drWB_PPAProduct = null;
        this._bIsIdEditable = true
    }

    get WB_PPAProduct(): WB_PPA_ProductRow {
        return this.drWB_PPAProduct;
    }

    get bIsIdEditable(): boolean {
        return this._bIsIdEditable
    }

    //<editor-fold desc = "IBusinessObject operations">

    LoadNew(options?: { [id: string]: number }): Promise<IBusinessObject> {
        const self = this;
        this.LoadProgressProvider.start("Creating PPA Product");
        this._OnLoadComplete = new Promise<IBusinessObject>((resolve, error) => {

            self.LoadOptions = options || {};
            if ('dbContext' in self.LoadOptions)
                self.dbContext = self.LoadOptions.dbContext;
            else
                self.dbContext = new PAdbContext();

            if (this.dbContext == null) {
                const sError = "Unable to determine dbContext for new Product";
                self._OnLoadComplete = null;
                self.LoadProgressProvider.exception(sError);
                error(sError);
                return;
            }

            // Create record using dbContext
            this.dbContext.createRecordWithKey(esWB_PPA_Product)
                .then((newPPAProductRow: WB_PPA_ProductRow) => {
                    newPPAProductRow.setDefaults();
                    newPPAProductRow.cif.WB_PPA_MODEL_ID = options.WB_PPA_MODEL_ID || 0
                    newPPAProductRow.cif.WB_PPA_PRICEPRDTYPE_ID = options.WB_PPA_PRICEPRDTYPE_ID || 0
                    newPPAProductRow.cif.WB_PROD_ID_REF = options.WB_PROD_ID_REF || 0
                    newPPAProductRow.cif.WB_PRDGRP_ID_REF = options.WB_PRDGRP_ID_REF || 0
                    newPPAProductRow.cif.SCENARIO_PRODUCT_IND = (options.SCENARIO_PRODUCT_IND || 0).toString()

                    self.drWB_PPAProduct = newPPAProductRow;
                    self.dbContext.add(esWB_PPA_Product, newPPAProductRow);
                    self.Identity.setAll(self.WB_PPAProduct.cif.WB_PPA_PRODUCT_ID, self.WB_PPAProduct.cif.PRODUDCT_ID, self.WB_PPAProduct.cif.PRODUCT_NAME);
                    self.LoadProgressProvider.setName("Loading " + self.Identity.name + "(" + self.Identity.id_ui + ")");
                    self.Identity.listenToRecordUpdates(self.WB_PPAProduct, 'WB_PPA_PRODUCT_ID', 'PRODUCT_ID', 'PRODUCT_NAME');
                    self.LoadProgressProvider.finish();

                    return self.dbContext.findAll(esWB_PPA_Attribute, {
                        where: {
                            DELETED_IND: { neq: '1' },
                            WB_PPA_MODEL_ID: newPPAProductRow.cif.WB_PPA_MODEL_ID
                        }
                    })
                })
                .then((arrAttribute: WB_PPA_AttributeRow[]) => {
                    return self.addPPAProductAttribute([], arrAttribute)
                })
                .then(() => {
                    self._bIsLoaded = true;
                    self._bIsIdEditable = true;
                    self._OnLoadComplete = null;
                    resolve(self);
                })
                .catch((err) => {
                    self.LoadProgressProvider.exception(err);
                    error(err)
                })
        });

        return this._OnLoadComplete;
    }

    Load(anId: number | number, options?: object): Promise<IBusinessObject> {

        const self = this;

        let recordResolver: ESRecordResolver;
        let recordResolverLoadPromise: Promise<void>;

        self.LoadProgressProvider.start("Loading (" + self.Identity.id_ui + ")");

        self._OnLoadComplete = new Promise<IBusinessObject>((resolve, error) => {

            if (self.bIsLoaded) {
                const sError = "PPA Product already loaded.";
                self._OnLoadComplete = null;
                self.LoadProgressProvider.exception(sError);
                error(sError);
                return;
            }

            self.LoadOptions = options || {};

            if ('dbContext' in self.LoadOptions)
                self.dbContext = self.LoadOptions.dbContext;
            else if (self.dbContext == null)
                self.dbContext = new PAdbContext();

            if (this.dbContext == null) {
                const sError = "Unable to determine dbContext for PPA Product";
                self._OnLoadComplete = null;
                self.LoadProgressProvider.exception(sError);
                error(sError);
                return;
            }

            if ('RecordResolver' in self.LoadOptions) {
                recordResolver = (<any>self.LoadOptions).RecordResolver;
                recordResolverLoadPromise = ASync.resolve()
            } else {
                recordResolver = PPAProductBO.createRecordResolver({ dbContext: self.dbContext });
                recordResolverLoadPromise = PPAProductBO.setupRecordResolver(recordResolver, [anId])
            }

            recordResolverLoadPromise
                .then(function() {
                    const newPPAProductRow: any = recordResolver.get(esWB_PPA_Product, anId);

                    if (_.isEmpty(newPPAProductRow)) {
                        self._OnLoadComplete = null;
                        self.LoadProgressProvider.exception("Id not found.");
                        resolve(self);
                        return;
                    }

                    self.drWB_PPAProduct = newPPAProductRow;
                    self.Identity.setAll(self.WB_PPAProduct.cif.WB_PPA_Product_ID, self.WB_PPAProduct.cif.Product_ID, self.WB_PPAProduct.cif.Product_NAME);
                    self.LoadProgressProvider.setName("Loading " + self.Identity.name + "(" + self.Identity.id_ui + ")");
                    self.Identity.listenToRecordUpdates(self.WB_PPAProduct, 'WB_PPA_Product_ID', 'Product_ID', 'Product_NAME');
                })
                .then((ignore: any) => {
                    // self._bIsIdEditable = !bExist
                    self._bIsLoaded = true;
                    self._OnLoadComplete = null;
                    self.LoadProgressProvider.finish();
                    resolve(self);
                })
                .catch((err: any) => {
                    self._OnLoadComplete = null;
                    self.LoadProgressProvider.exception(err);
                    error(err);
                });

        });

        return this._OnLoadComplete;
    }

    get OnLoadComplete(): Promise<IBusinessObject> {
        return this._OnLoadComplete;
    }

    get bIsLoaded(): boolean {
        return this._bIsLoaded;
    }

    async Validate(options?: any): Promise<IBusinessObject> {

        const self = this;

        self.ValidateMessageProvider.clearMessages();
        self.ValidateProgressProvider.start("Validating " + self.WB_PPAProduct.cif.Product_NAME + "(" + self.WB_PPAProduct.cif.Product_ID + ")");

        // Does no validations yet
        self.ValidateOptions = options || {};
        try {
            if (self.dbContext == undefined) {
                const sError = "Unable to determine dbContext to Validate PPA Product Data Source.";
                self.ValidateProgressProvider.exception(sError);
                self.ValidateMessageProvider.addMessage({
                    code: "nodbcontext",
                    type: MessageType.Error,
                    title: sError,
                    description: "",
                    subtitle: "",
                    count: 0
                })
            }

            if (!(self.drWB_PPAProduct.cif.WB_PPA_MANUFACTURER_ID > 0) ||
                !(self.drWB_PPAProduct.cif.WB_PPA_BRAND_ID > 0) ||
                !(self.drWB_PPAProduct.cif.LIST_PRICE > 0) ||
                !(self.drWB_PPAProduct.cif.RETAIL_PRICE > 0) ||
                !(self.drWB_PPAProduct.cif.EQV_FACTOR > 0) ||
                !(self.drWB_PPAProduct.cif.COGS > 0) ||
                _.isEmpty(self.drWB_PPAProduct.cif.PRODUCT_ID) ||
                _.isEmpty(self.drWB_PPAProduct.cif.PRODUCT_NAME)
            ) {
                const sError = "Required fields cannot be empty";
                self.ValidateProgressProvider.exception(sError);
                self.ValidateMessageProvider.addMessage({
                    code: "fieldempty",
                    type: MessageType.Error,
                    title: sError,
                    description: "",
                    subtitle: "",
                    count: 0
                })
            }

            // await ModelPredictorBOValidations.ValidateFields(self)

            self._bIsValidated = self.ValidateMessageProvider.countMessages(MessageType.Error) <= 0;
            self.ValidateProgressProvider.finish();
        } catch (err) {
            self._bIsValidated = false;
            self.ValidateProgressProvider.exception(err);
            return self;
        }

        return self
    }

    get bIsValidated(): boolean {
        return this._bIsValidated;
    }

    Save(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(false, options)
    }

    SaveBatch(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(true, options)
    }

    async SaveUsing(bBatch: boolean, options?: any): Promise<IBusinessObject> {
        const self = this;

        const wbPPAProduct = <IwbPPAProduct>self.drWB_PPAProduct;
        try {
            self.SaveProgressProvider.start("Saving " + wbPPAProduct.PRODUCT_NAME + "(" + wbPPAProduct.PRODUCT_ID + ")");
            self._bIsSaved = false;
            self.SaveOptions = options || {};

            if (_.isEmpty(self.dbContext)) {
                const sError = "Unable to determine dbContext to Save PPA Product";
                self.SaveProgressProvider.exception(sError);
                return
            }

            let saveFunction: (esName: string, saveRecords: ESRecord[] | ESRecord, options?: any) => Promise<ESDataStore>;
            if (bBatch) {
                saveFunction = self.dbContext.SaveBatch.bind(self.dbContext);
            } else {
                saveFunction = self.dbContext.Save.bind(self.dbContext);
            }


            await self.Validate({})

            if (!self.bIsValidated) {
                self._bIsSaved = false;
                self.SaveProgressProvider.finish();
                return self;
            }

            await saveFunction(esWB_PPA_Product, self.drWB_PPAProduct, {})

            const tasks: Promise<any>[] = []

            tasks.push(saveFunction(esWB_PPA_Product_Attribute, self.drWB_PPAProduct.WB_PPAProductAttributeRows))
            await Promise.all(tasks)
            if (bBatch) {
                self.dbContext.addBatchCompleteListener(() => {
                    self._bIsSaved = true;
                })
            } else {
                self._bIsSaved = true;
            }

            self._bIsIdEditable = false
            self.SaveProgressProvider.finish();
            return self;
        } catch (err) {
            self.SaveProgressProvider.exception(err);
            return (self)
        }

    }

    get bIsSaved(): boolean {
        return this._bIsSaved;
    }

    //Delete
    Delete(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(true, options)
    }

    DeleteOnSave(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(false, options)
    }

    private DeleteUsing(bSaveNow: boolean, options?: any): Promise<IBusinessObject> {
        const self = this;
        const wbPPAProduct = <IwbPPAProduct>self.drWB_PPAProduct;
        this.DeleteMessageProvider.clearMessages();
        this.DeleteProgressProvider.start("Deleting " + wbPPAProduct.SPEND_FUNCTION_NAME + "(" + wbPPAProduct.SPEND_FUNCTION_ID + ")");

        return new Promise<IBusinessObject>((resolve, error) => {

            // 1. Validate if we can delete the Model Predictor

            // 2. If Valid, mark all the records for deletion.
            self.drWB_PPAProduct.markDeleted();

            if (bSaveNow) {
                self.SaveUsing(false)
                    .then((ignore) => {
                        self._bIsDeleted = true;
                        self.DeleteProgressProvider.finish();
                        resolve(self);
                    })
                    .catch((err) => {
                        this.DeleteProgressProvider.exception(err);
                        resolve(self)
                    })
            } else {
                self._bIsDeleted = true;
                self.DeleteProgressProvider.finish();
                resolve(self);
            }
        });
    }

    get bIsDeleted(): boolean {
        return this._bIsDeleted;
    }

    addPPAProductAttribute(attributeWbIds: number[], arrAttributes: WB_PPA_AttributeRow[]): Promise<PPAProductBO> {
        const self = this;
        if (_.isEmpty(attributeWbIds) && _.isEmpty(arrAttributes))
            return
        return new Promise<PPAProductBO>(async (resolve, error) => {
            if (_.isEmpty(attributeWbIds))
                attributeWbIds = _.map(arrAttributes, 'WB_PPA_ATTRIBUTE_ID')
            const existingAttributes = _.map(_.filter(self.WB_PPAProduct.WB_PPAProductAttributeRows, row => row.isActive), "WB_PPA_ATTRIBUTE_ID")
            attributeWbIds = _.difference(attributeWbIds, existingAttributes)
            const newIds = await self.dbContext.getNewIds(esWB_PPA_Product_Attribute, attributeWbIds.length);

            if (_.isEmpty(arrAttributes)) {
                arrAttributes = await self.dbContext.findAll(esWB_PPA_Attribute, {
                        where: {
                            DELETED_IND: { neq: '1' },
                            WB_PPA_ATTRIBUTE_ID: { inq: attributeWbIds }
                        }
                    }
                )
            } else {
                arrAttributes = _.filter(arrAttributes, a => _.includes(attributeWbIds, a.cif.WB_PPA_ATTRIBUTE_ID))
            }
            _.forEach(arrAttributes, (attr, idx) => {
                const aNewRow: WB_PPA_Product_AttributeRow = <WB_PPA_Product_AttributeRow>self.dbContext.createRecord(esWB_PPA_Product_Attribute)
                aNewRow.setDefaults();
                aNewRow.cif.WB_PPA_PRODUCT_ATTRIBUTE_ID = Number(newIds[idx])
                aNewRow.WB_PPAAttributeRow = attr
                aNewRow.cif.WB_PPA_ATTRIBUTE_ID = attr.cif.WB_PPA_ATTRIBUTE_ID
                aNewRow.cif.WB_PPA_Product_ID = self.drWB_PPAProduct.cif.WB_PPA_Product_ID;
                aNewRow.WB_PPAProductRow = self.drWB_PPAProduct

                self.dbContext.add(esWB_PPA_Product_Attribute, aNewRow);
            })
            resolve(self)
        })

    }

    removePPAProductAttribute(aPrdId: number): Promise<void> {
        const self = this
        return new Promise<void>((resolve, error) => {
            _.forEach(self.drWB_PPAProduct.WB_PPAProductAttributeRows, (x: WB_PPA_Product_AttributeRow, index: number) => {
                if (x.isActive && x.cif.WB_PPA_PRODUCT_ATTRIBUTE_ID == aPrdId) {
                    x.markDeleted()
                }
            })
            resolve()
        })
    }

    static createRecordResolver(boLoadOptions: any): ESRecordResolver {

        const opts = boLoadOptions || {};
        let dbContext: PAdbContext;

        if ('dbContext' in opts) {
            dbContext = opts.dbContext;
        } else {
            dbContext = new PAdbContext({});
        }

        const PPAProduct_options: CreateOptions = {
            standardConditions: {},
            dctIncludeBelongsToNamedRelations: {}
        };

        const aRecResolver = new ESRecordResolver(dbContext, PPAProduct_options);

        return aRecResolver;
    }

    static setupRecordResolver(recordResolver: ESRecordResolver, ids: string[] | number[]): Promise<void> {

        return new Promise<void>((resolve, error) => {
            recordResolver.findAllWithRelationships(esWB_PPA_Product, ids).then((result) => {
                resolve()
            }).catch((err) => {
                error(err)
            })
        })
    }

    static concurrentLoadCountHint(): number {
        return 10;
    }

    //</editor-fold>
}

PAEnvironment.registerBusinessObjectClass(PPAProductBO, "PPAProductBO", "PPA Product")
